'use client';

import { HStack, VStack } from '@terra/ui-styled-system';
import { Add01Icon, Delete01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { GuardrailRule } from '@terra/shared-contracts-applications';
import { Button } from '../../components/button';
import { Field } from '../../components/field';
import { IconButton } from '../../components/icon-button';
import { Input } from '../../components/input';
import {
  appendGuardrailRule,
  canAppendGuardrailRule,
  removeGuardrailRule,
  updateGuardrailRule,
} from './guardrail-form.model';

type GuardrailRulesFieldProps = {
  rules: GuardrailRule[];
  errors?: Record<number, string>;
  onRulesChange: (rules: GuardrailRule[]) => void;
  onRuleEdited: (index: number) => void;
};

export const GuardrailRulesField = ({
  rules,
  errors,
  onRulesChange,
  onRuleEdited,
}: GuardrailRulesFieldProps) => {
  const t = useTranslations('designSystem.guardrails');

  return (
    <Field.Root w="full">
      <Field.Label>{t('form.rules')}</Field.Label>
      <Field.HelperText>{t('form.rulesHelper')}</Field.HelperText>
      <VStack gap={3} alignItems="flex-start" w="full">
        {rules.map((rule, index) => (
          <HStack key={index} gap={2} w="full" alignItems="flex-start">
            <Field.Root flex="1" invalid={Boolean(errors?.[index])}>
              <Input
                value={rule.rule}
                onChange={(event) => {
                  onRulesChange(
                    updateGuardrailRule({
                      rules,
                      index,
                      value: event.target.value,
                    }),
                  );
                  onRuleEdited(index);
                }}
                placeholder={t('form.rulePlaceholder')}
                size="sm"
              />
              {errors?.[index] ? (
                <Field.ErrorText>{errors[index]}</Field.ErrorText>
              ) : null}
            </Field.Root>
            {index > 0 ? (
              <IconButton
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={() =>
                  onRulesChange(removeGuardrailRule({ rules, index }))
                }
                aria-label={t('form.removeRule')}
              >
                <Delete01Icon size={14} />
              </IconButton>
            ) : null}
          </HStack>
        ))}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onRulesChange(appendGuardrailRule(rules))}
          disabled={!canAppendGuardrailRule(rules)}
        >
          <Add01Icon size={14} />
          {t('form.addRule')}
        </Button>
      </VStack>
    </Field.Root>
  );
};
